import { Extension } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'

// prosemirror-view's record of an iOS Enter (editHandlers.keydown): preventing
// the key confuses the iOS keyboard, so it lets WebKit insert the new line and
// runs the editor's Enter in its place when it reads that DOM change, within
// 225ms of the key, or after 200ms if no change comes.
type IOSEnterInput = {
    lastIOSEnter?: number
    lastIOSEnterFallbackTimeout?: number
}

export type IOSEnterView = Pick<EditorView, 'dom' | 'someProp'> & {
    input?: IOSEnterInput
    domObserver?: { flush: () => void; forceFlush: () => void }
}

export type CaretView = Pick<EditorView, 'dom' | 'composing' | 'hasFocus' | 'isDestroyed'>

export const IOS_ENTER_WINDOW_MS = 225

const BOTTOM_EDITOR_PILL = '[data-bottom-editor-pill="true"]'

// The key prosemirror-view gives the Enter handlers in place of the real one
// (its keyEvent(13, 'Enter')).
function enterKeyEvent(): KeyboardEvent {
    const event = document.createEvent('Event')
    event.initEvent('keydown', true, true)
    return Object.assign(event, { keyCode: 13, key: 'Enter', code: 'Enter' }) as KeyboardEvent
}

// WebKit's own new line reveals itself by scrolling the page. In the bottom
// editor the dock puts the page straight back, and WebKit then draws the caret
// a line above where it is, so an Enter looks swallowed until a later one
// catches up. Once the key has gone through (so the keyboard saw it), cancel
// the native new line at beforeinput and run the editor's Enter now: the same
// Enter, with nothing for WebKit to reveal.
export function runIOSEnterInPlace(
    view: IOSEnterView,
    event: InputEvent,
    now: number = Date.now(),
): boolean {
    const input = view.input
    if (event.inputType !== 'insertParagraph' || event.isComposing || !input) return false
    // Only an Enter prosemirror-view has left to WebKit: on iOS, from the
    // key, outside a composition.
    const enterAt = input.lastIOSEnter
    if (typeof enterAt !== 'number' || !enterAt || enterAt <= now - IOS_ENTER_WINDOW_MS) {
        return false
    }
    if (!view.dom.closest(BOTTOM_EDITOR_PILL)) return false

    // What the keyboard changed before the key (an autocorrection) goes in
    // first and as itself, as prosemirror-view's own keydown reads it.
    input.lastIOSEnter = 0
    view.domObserver?.forceFlush()
    view.domObserver?.flush()
    if (!view.someProp('handleKeyDown', f => f(view as EditorView, enterKeyEvent()))) {
        // Nothing took the key: WebKit's new line, read as before.
        input.lastIOSEnter = enterAt
        return false
    }

    event.preventDefault()
    clearTimeout(input.lastIOSEnterFallbackTimeout)
    return true
}

// The lines a reply is written in: its paragraphs (and other text blocks) and
// the breaks inside them.
export function countLines(doc: PMNode): number {
    let lines = 0
    doc.descendants(node => {
        if (node.isTextblock || node.type.name === 'hardBreak') lines += 1
        return !node.isLeaf
    })
    return lines
}

// Sets the DOM selection again, as it is.
function reseatCaret(view: CaretView): boolean {
    if (view.isDestroyed || view.composing || !view.hasFocus()) return false
    const selection = view.dom.ownerDocument.getSelection()
    if (!selection?.rangeCount) return false
    const range = selection.getRangeAt(0).cloneRange()
    if (!view.dom.contains(range.startContainer)) return false
    selection.removeAllRanges()
    selection.addRange(range)
    return true
}

// WebKit on iOS redraws the caret only when its spot on screen changes. An
// Enter or a Backspace on the pill's last line grows or shrinks the pill,
// anchored at its foot, by a line (at its full height, scrolls its field by
// one) and leaves the caret's spot where it was, so WebKit keeps the caret
// where it drew it and it moves with the text: a line off, in the line that
// took its place. Once the pill has settled, set the selection again with the
// text a pixel aside, then again without: two spots WebKit hasn't drawn the
// caret at, so it places it afresh.
export function redrawCaretAfterLayout(
    view: CaretView,
    isCurrent: () => boolean = () => true,
    frame: (callback: () => void) => unknown = requestAnimationFrame,
) {
    frame(() =>
        frame(() => {
            if (!isCurrent()) return
            const style = view.dom.style
            style.left = '1px'
            if (!reseatCaret(view)) {
                style.left = ''
                return
            }
            frame(() => {
                style.left = ''
                if (isCurrent()) reseatCaret(view)
            })
        }),
    )
}

export const IOSBottomEditorCaretPlugin = Extension.create<{ ios: boolean }>({
    name: 'iosBottomEditorCaret',

    addOptions() {
        return { ios: false }
    },

    addProseMirrorPlugins() {
        const { ios } = this.options
        return [
            new Plugin({
                key: new PluginKey('iosBottomEditorCaret'),
                props: {
                    handleDOMEvents: {
                        beforeinput: (view, event) =>
                            runIOSEnterInPlace(view as IOSEnterView, event as InputEvent),
                    },
                },
                // A change of line count in the focused bottom editor: an
                // Enter, a Backspace that joins lines, a paste.
                view: () => {
                    let redraw = 0
                    return {
                        update: (view, prevState) => {
                            if (!ios || view.state.doc === prevState.doc) return
                            if (!view.hasFocus() || !view.dom.closest(BOTTOM_EDITOR_PILL)) return
                            if (countLines(view.state.doc) === countLines(prevState.doc)) return
                            const current = ++redraw
                            redrawCaretAfterLayout(view, () => current === redraw)
                        },
                        destroy: () => {
                            redraw += 1
                        },
                    }
                },
            }),
        ]
    },
})

export default IOSBottomEditorCaretPlugin
